/** Watch (/watch/:videoId): the frame-exact clip editor on the MV clock. View state in the URL:
 * t, in, out, loop, rate, lanes, zoom, text (masked|raw), beats (beats-table layer filter). */
import { useCallback, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { qk } from "../../api/keys";
import { ErrorBox } from "../../components/ErrorBox";
import { useAnnounce } from "../../components/announce";
import { PageHeader } from "../../components/PageHeader";
import { isAgentMode } from "../../lib/agent";
import { Loading, Steps, useLiveInvalidate } from "../common";
import { PlayerStatus, Transport, VideoSurface, type Describe, type InOut } from "../player/Player";
import { useMediaInfo, usePlayer, useUrlPlayerSync } from "../player/usePlayer";
import { Lanes, useLaneView } from "../timeline/Lanes";
import { durationOf, grids, lanesParam, lineAt, lineText, parseLanes, sectionAt, type Lane, type TextMode }
  from "../timeline/project";
import type { Timeline } from "../types";
import { useUrlState, type UrlPatch } from "../url";
import { BeatsTable } from "./BeatsTable";
import { ClipsPanel } from "./ClipsPanel";
import { CutsTable } from "./CutsTable";
import { timelineKey, useTimeline, useVideoTitle } from "./data";
import { HeatTable } from "./HeatTable";
import { LyricsPanel } from "./LyricsPanel";

export function WatchView() {
  const { videoId } = useParams();
  const id = Number(videoId);
  const tl = useTimeline(id);
  useLiveInvalidate((e) => (e.entity === "video" && e.entity_id === id) || e.entity === "clip",
    [timelineKey(id), qk.subitems("videos", id)]);
  if (!Number.isInteger(id) || id <= 0) return <ErrorBox error={new Error(`bad video id ${String(videoId)}`)} />;
  if (tl.isPending) return <><PageHeader title={`Watch video #${id}`} /><Loading what="timeline" /></>;
  if (tl.isError) {
    return (
      <section>
        <PageHeader title={`Watch video #${id}`} md={{ page: "video", params: { id } }} />
        <ErrorBox error={tl.error} />
      </section>
    );
  }
  return <WatchLoaded tl={tl.data} />;
}

function WatchLoaded({ tl }: { tl: Timeline }) {
  const id = tl.video_id;
  const url = useUrlState();
  const agent = isAgentMode();
  const title = useVideoTitle(id);
  const media = useMediaInfo(tl.preview_media_id);
  const fps = tl.fps ?? media.data?.fps ?? 30;
  const fpsKnown = tl.fps != null || media.data?.fps != null;
  const durationMs = durationOf(tl);
  const { ctrl, videoRef } = usePlayer({ mediaKey: tl.preview_media_id ?? null, fps, durationMs: tl.duration_ms ?? null,
    frameCount: media.data?.frames ?? null });
  useUrlPlayerSync(ctrl, url);

  const mode: TextMode = url.str("text") === "raw" ? "raw" : "masked";
  const lanesStr = url.str("lanes");
  const lanes = useMemo(() => parseLanes(lanesStr), [lanesStr]);
  const zoom = url.int("zoom") ?? 0;
  const view = useLaneView(ctrl, zoom, durationMs);
  const g = useMemo(() => grids(tl), [tl]);
  const announce = useAnnounce();
  // the router's setter changes identity on every URL write (the playhead writes t=): one stable setter, so lanes,
  // the waveform and In/Out consumers do not rebuild on every tick
  const urlSet = useRef(url.set);
  urlSet.current = url.set;
  const set = useCallback((p: UrlPatch) => urlSet.current(p), []);
  const inMs = url.int("in");
  const outMs = url.int("out");
  const loop = url.flag("loop");
  const inOut: InOut = useMemo(() => ({ inMs, outMs, loop, set }), [inMs, outMs, loop, set]);
  const describe: Describe = useCallback((t) => {
    const l = lineAt(tl.lines, t);
    return { section: sectionAt(tl.sections, t)?.label ?? null, line: l ? lineText(l, mode) : null };
  }, [tl, mode]);

  const name = title.data?.title ?? `Video #${id}`;
  return (
    <section className="ws8-page" data-testid="watch-page">
      <PageHeader title={`Watch: ${name}`} md={{ page: "video", params: { id } }}>
        {tl.song_id != null && <Link className="button" to={`/songs/${tl.song_id}`}>Song page</Link>}
      </PageHeader>
      <Steps steps={tl.steps} />
      {!fpsKnown && <p className="ws8-warn" role="note">Frame rate unknown; assuming 30 fps.</p>}
      <div className="ws8-watch-top">
        <div className="ws8-player-col">
          <VideoSurface ctrl={ctrl} videoRef={videoRef} mediaId={tl.preview_media_id} webmId={tl.preview_webm_media_id}
            label="MV preview" vcodec={media.data?.vcodec} />
          <PlayerStatus ctrl={ctrl} describe={describe} />
        </div>
        <LyricsPanel tl={tl} ctrl={ctrl} mode={mode} setMode={(m) => set({ text: m === "raw" ? "raw" : null })}
          agent={agent} inOut={inOut} announce={announce} />
      </div>
      <Transport ctrl={ctrl} grids={g} inOut={inOut} rateParam={(r) => set({ rate: r === 1 ? null : r })} />
      <Lanes tl={tl} durationMs={durationMs} ctrl={ctrl} lanes={lanes} zoom={zoom} mode={mode} inOut={inOut}
        setLanes={(next: Set<Lane>) => set({ lanes: lanesParam(next) })} setZoom={(s) => set({ zoom: s || null })}
        peaksMediaId={tl.waveform_media_id ?? null}
        audioMediaId={tl.preview_webm_media_id ?? tl.preview_media_id ?? null} />
      <BeatsTable tl={tl} ctrl={ctrl} view={view} lanes={lanes} agent={agent} filter={url.str("beats")}
        setFilter={(f) => set({ beats: f })} />
      <CutsTable tl={tl} ctrl={ctrl} view={view} agent={agent} inOut={inOut} />
      <div className="ws8-watch-bottom">
        <ClipsPanel tl={tl} ctrl={ctrl} fps={fps} inOut={inOut} />
        <HeatTable tl={tl} ctrl={ctrl} />
      </div>
    </section>
  );
}
